import React, { createContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@stadium/services/auth0";
import { useQuery } from "@stadium/services/apollo-client";
import { decodeToken } from "@stadium/helpers/token";
import get from "lodash/get";
import pick from "lodash/pick";

import { selectAllCountries } from "@stadium/redux-utils/selectors";
import { getCountryDataFromName } from "@helpers/config";
import { fetchUser } from "graphql/queries/user/basic-details";
import { updateUserGeolocationParams } from "@actions/user";

const defaultCurrentUserData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  companyName: "",
  countryName: "",
  spreeId: "",
  walletBalance: "",
  walletCurrency: {
    id: "",
    code: "",
    name: "",
    symbol: "",
  },
};
const SSO_ENABLED_URL = "https://bystadium.com/oauth/claims/ssoEnabled";

const USER_DATA_FIELDS = Object.keys(defaultCurrentUserData);

export const CurrentUserContext = createContext({
  loading: false,
  currentUserData: defaultCurrentUserData,
  isSSOEnabled: false,
  baseCountry: {
    iso: "",
    name: "",
    international: false,
  },
  ldData: {
    userKey: "",
    secureModeHash: "",
  },
  // eslint-disable-next-line no-unused-vars
  setCurrentUserData: (data) => null,
});

const CurrentUserProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [currentUserData, setCurrentUserData] = useState(defaultCurrentUserData);
  const [isSSOEnabled, setIsSSOEnabled] = useState(false);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { data, error, loading } = useQuery(fetchUser, { skip: !isAuthenticated });
  const { countryName } = currentUserData;
  const countries = useSelector(selectAllCountries);

  useEffect(() => {
    if (!loading && !error) {
      const response = get(data, "currentUser");
      const userData = pick(response, USER_DATA_FIELDS);

      setCurrentUserData(userData);
    }
  }, [loading, error, data]);

  //set user's default country details from ipStack
  useEffect(() => {
    if (isAuthenticated && !loading) {
      dispatch(updateUserGeolocationParams(countryName));
    }
  }, [countryName, isAuthenticated, loading, dispatch]);

  const ldData = useMemo(() => {
    const userKey = get(data, "currentUser.ldUserKey");
    const secureModeHash = get(data, "currentUser.ldSecureModeHash");

    return { userKey, secureModeHash };
  }, [data]);

  const baseCountry = useMemo(
    () => getCountryDataFromName(countries, countryName),
    [countryName, countries]
  );

  useEffect(() => {
    getAccessTokenSilently()
      .then((token) => decodeToken(token))
      .then((data) => {
        const isSSOEnabled = data[SSO_ENABLED_URL];
        setIsSSOEnabled(isSSOEnabled);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
        setIsSSOEnabled(false);
      });
  }, [getAccessTokenSilently]);

  const value = useMemo(() => {
    return {
      loading,
      currentUserData,
      isSSOEnabled,
      baseCountry,
      setCurrentUserData,
      ldData,
    };
  }, [loading, currentUserData, isSSOEnabled, baseCountry, setCurrentUserData, ldData]);

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};

export default CurrentUserProvider;
